import { Box, LinearProgress } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useListCallbackRef } from "react-window";
import { VirtualizedList } from "../../../../components/UIToolkit/molecules/VirtualizedList";
import { WeekStartDaySelector } from "../../../../components/WeekStartDaySelector/WeekStartDaySelector";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "../../../../contexts/FeatureFlagsProvider";
import { useNurseCallInsByStaffingDate } from "../../../../hooks/useCallIns";
import { useLocalizedWeekDays } from "../../../../hooks/useDateLocalization";
import { useDepartmentFlags } from "../../../../hooks/useDepartmentFlags";
import { useListScrollbarWidth } from "../../../../hooks/useListScrollbarWidth";
import { useFeatureFlag } from "../../../../hooks/useOpenFeatureFlag";
import { INK, SURFACE_TINT } from "../../../../theme/tokens";
import { getStaffingDate } from "../../../../utils/dateUtils";
import { isNurseActiveInDepartment } from "../../../../utils/formatters";
import type { Nurse, WeekData } from "../../../../utils/models";
import { useDaysOffAndPtoState } from "../DaysOffAndPTO/useDaysOffAndPtoState";
import { WeekStaffScheduleExportButton } from "./WeekStaffScheduleExport/WeekStaffScheduleExportButton";
import {
	buildDayOffByDateStr,
	buildPreferenceByDateStr,
	DAYS_PER_WEEK,
	resolveWeekScheduleRow,
	type WeekMeta,
	type WeekScheduleRowContext,
} from "./weekScheduleCellResolver";
import { DayCell } from "./XWeekScheduleGrid/DayCell/DayCell";

type Props = {
	weeks: WeekMeta[];
	// The SINGLE CONTIGUOUS weeks array from the nurse schedule query (Sunday-aligned). Cell lookups index this
	// directly via `absoluteWeekIndex`, so the `+1` spillover hits the calendar-next week's data.
	weeksData: WeekData[];
	nurseFromProfile: Nurse;
	onCreatedSingleShift?: () => void;
	// Called when the first / last row comes into view, to load the weeks past it.
	onReachStart: () => void;
	onReachEnd: () => void;
	isLoadingMoreWeeks: boolean;
	viewportHeight?: number | string;
	// Days the visible week is shifted past Sunday (the data-week start). 0 = Sunday-aligned
	// (flag off / default) → identity lookups + unrotated labels, i.e. unchanged behavior.
	offset?: number;
	// Toolbar affordances. The dynamic-week-start FF is checked here, so callers only express intent.
	showWeekStartDaySelector: boolean;
	showExportButton: boolean;
};

const ROW_HEIGHT = 118;
const HEADER_HEIGHT = 25;
const VISIBLE_ROWS = 4;
const PADDING_PX = 2;
// Loading starts this many rows before the edge, so the next weeks usually land before the user gets there.
const EDGE_ROWS = 1;
// Header is rendered outside the list, so the viewport height maps directly to N row slots.
const DEFAULT_VIEWPORT_HEIGHT = VISIBLE_ROWS * ROW_HEIGHT;

export const VirtualizedWeekScheduleGrid: FC<Props> = ({
	weeks,
	weeksData,
	nurseFromProfile,
	onCreatedSingleShift,
	onReachStart,
	onReachEnd,
	isLoadingMoreWeeks,
	viewportHeight = DEFAULT_VIEWPORT_HEIGHT,
	offset = 0,
	showWeekStartDaySelector,
	showExportButton,
}) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { isMedicalDepartment } = useDepartmentFlags();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const weekDaysLabels = useLocalizedWeekDays("cccc");
	const [list, setList] = useListCallbackRef(null);
	const didInitialScrollRef = useRef(false);
	const scrollbarWidth = useListScrollbarWidth(list);

	// Today's staffing date (respects dept dayStart wraparound for night-shift boundaries).
	const todayStaffingDate = useMemo(
		() => getStaffingDate(DateTime.now().setZone(chosenDepartment.timezone), chosenDepartment.dayStart),
		[chosenDepartment.timezone, chosenDepartment.dayStart],
	);
	const todayStr = useMemo(() => todayStaffingDate.toFormat("yyyy-MM-dd"), [todayStaffingDate]);

	// Find the row index whose week contains today, so we land on it after mount.
	const scrollToIndex = useMemo(() => {
		for (let i = 0; i < weeks.length; i++) {
			const ws = weeks[i].weekStart;
			if (ws <= todayStaffingDate && todayStaffingDate < ws.plus({ weeks: 1 })) {
				return i;
			}
		}
		return -1;
	}, [weeks, todayStaffingDate]);

	useEffect(() => {
		if (didInitialScrollRef.current || !list) return;
		if (scrollToIndex <= 0) {
			// Nothing to scroll to; the edge loading below can start right away.
			didInitialScrollRef.current = true;
			return;
		}
		// Wait one frame so react-window finishes its initial layout/measurement
		// before we try to scroll — otherwise scrollToRow silently no-ops.
		const raf = requestAnimationFrame(() => {
			list.scrollToRow({ index: scrollToIndex, align: "start", behavior: "auto" });
			didInitialScrollRef.current = true;
		});
		return () => cancelAnimationFrame(raf);
	}, [scrollToIndex, list]);

	// Weeks loaded above the current ones push every row down; move the scroll by the same amount so the
	// rows on screen stay where they were instead of jumping to later weeks.
	const firstWeekKeyRef = useRef<string | null>(null);
	useLayoutEffect(() => {
		const firstWeekKey = weeks[0]?.weekStart.toISODate() ?? null;
		const previousFirstWeekKey = firstWeekKeyRef.current;
		firstWeekKeyRef.current = firstWeekKey;
		const element = list?.element;
		if (!element || !previousFirstWeekKey || previousFirstWeekKey === firstWeekKey) return;
		const addedAbove = weeks.findIndex((week) => week.weekStart.toISODate() === previousFirstWeekKey);
		if (addedAbove > 0) element.scrollTop += addedAbove * ROW_HEIGHT;
	}, [weeks, list]);

	const handleRowsRendered = useCallback(() => {
		const element = list?.element;
		// Before the first scroll to today the list sits at row 0, which would read as "reached the start".
		if (!element || !didInitialScrollRef.current) return;
		// Read from the scroll position, not from the rows the list reports: right after weeks are added above,
		// it reports its last scroll state (row 0) once more, which would load another page for nothing.
		const firstVisibleRow = Math.floor(element.scrollTop / ROW_HEIGHT);
		const lastVisibleRow = Math.floor((element.scrollTop + element.clientHeight - 1) / ROW_HEIGHT);
		if (firstVisibleRow <= EDGE_ROWS) onReachStart();
		if (lastVisibleRow >= weeks.length - 1 - EDGE_ROWS) onReachEnd();
	}, [list, weeks.length, onReachStart, onReachEnd]);

	const { daysOff } = useDaysOffAndPtoState({ nurse: nurseFromProfile });

	const dayOffByDateStr = useMemo(
		() => buildDayOffByDateStr(daysOff, chosenDepartment.timezone, chosenDepartment.dayStart),
		[daysOff, chosenDepartment.timezone, chosenDepartment.dayStart],
	);

	const preferenceByDateStr = useMemo(
		() => buildPreferenceByDateStr(nurseFromProfile?.employeeSpecificPreferences ?? []),
		[nurseFromProfile?.employeeSpecificPreferences],
	);

	// Medical departments don't record call-ins in IHH, which is why the profile hides the call-ins
	// section for them (`ScheduleSection`). Skipping the fetch here keeps the calendar consistent with
	// that instead of marking cells from data the rest of the page says doesn't exist.
	const { callInsByDate } = useNurseCallInsByStaffingDate({ nurseId: nurseFromProfile?.id, enabled: !isMedicalDepartment });

	const rowContext = useMemo<WeekScheduleRowContext>(
		// Single snapshot of everything a cell resolution needs. Shared with the export so both
		// surfaces render from identical data; its identity changes on exactly the same events as
		// the individual dependencies did before.
		() => ({
			weeksData,
			nurse: nurseFromProfile,
			departmentId: chosenDepartment.department_id,
			offset,
			todayStaffingDate,
			todayStr,
			dayOffByDateStr,
			preferenceByDateStr,
			callInsByDateStr: callInsByDate,
		}),
		[weeksData, nurseFromProfile, chosenDepartment.department_id, offset, todayStaffingDate, todayStr, dayOffByDateStr, preferenceByDateStr, callInsByDate],
	);

	const getDisabledTooltipMessage = useCallback(
		(currentDate: DateTime, nurse: Nurse | undefined) => {
			// Per-department active status, not the legacy global nurse.active — a float-pool rehire
			// inactive at a sibling facility must stay editable here (APP-278).
			if (!nurse || !isNurseActiveInDepartment(nurse, chosenDepartment.department_id)) return t("inactive");
			if (nurse.scheduledDeactivationDate && currentDate >= nurse.scheduledDeactivationDate.startOf("day")) return t("schedule.deactivated");
			// Full-day PTO no longer locks the cell — scheduling a shift on a PTO day is allowed and
			// cancels the overlapping PTO on the backend (DAT-156). The PTO indicator still renders via `dayOff`.
			return undefined;
		},
		[t, chosenDepartment.department_id],
	);

	const renderWeekRow = useCallback(
		(week: WeekMeta, weekRowIndex: number) => {
			const cells = resolveWeekScheduleRow(week, rowContext);
			return (
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(7, 1fr)",
						// Pin the row track to the slot height so cells never grow past their react-window
						// slot — overflowing shift content is clipped inside the cell instead of spilling
						// under the next row (which would hide the "today" cell's bottom border).
						gridAutoRows: `${ROW_HEIGHT - PADDING_PX}px`,
						gap: `${PADDING_PX}px`,
						width: "100%",
						height: `${ROW_HEIGHT - PADDING_PX}px`,
					}}
				>
					{cells.map((cell, dayIndex) => {
						const disabledTooltipMessage = getDisabledTooltipMessage(cell.currentDate, nurseFromProfile);
						return (
							<DayCell
								cellIndex={weekRowIndex * DAYS_PER_WEEK + dayIndex}
								key={cell.currentDate.toISO()}
								currentDate={cell.currentDate}
								nurseInShiftArray={cell.scheduledShifts}
								actualNurseInShiftArray={cell.actualShifts}
								viewOnly={cell.effectiveViewOnly}
								nurseFromProfile={nurseFromProfile}
								disabled={!!disabledTooltipMessage}
								tooltipTitle={disabledTooltipMessage}
								onCreatedSingleShift={onCreatedSingleShift}
								dayPreference={cell.dayPreference}
								dayOff={cell.dayOff}
								callIns={cell.callIns}
								isToday={cell.currentDayStr === todayStr}
							/>
						);
					})}
				</Box>
			);
		},
		[rowContext, nurseFromProfile, getDisabledTooltipMessage, onCreatedSingleShift, todayStr],
	);

	// The selector is meaningless without the dynamic-week-start flag, so the flag gate lives here
	// rather than at every call site.
	const showSelector = showWeekStartDaySelector && isDynamicWeekStartEnabled;

	return (
		<Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
			{/* Skip the row entirely when neither affordance shows, so it never reserves empty space. */}
			{(showSelector || showExportButton) && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1, mb: 1 }}>
					{showExportButton && <WeekStaffScheduleExportButton weeks={weeks} rowContext={rowContext} dayStart={chosenDepartment.dayStart} offset={offset} />}
					{showSelector && <WeekStartDaySelector source="profile" />}
				</Box>
			)}
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(7, 1fr)",
					gap: `${PADDING_PX}px`,
					height: `${HEADER_HEIGHT}px`,
					marginBottom: `${PADDING_PX}px`,
					boxSizing: "border-box",
					// Reserve the scrollbar gutter so header columns line up with the scrolled rows below.
					paddingRight: `${scrollbarWidth}px`,
				}}
			>
				{Array.from({ length: DAYS_PER_WEEK }, (_, index) => {
					// weekDaysLabels is Sunday-indexed (0=Sun..6=Sat); rotate by the same offset as the
					// data lookup so the column header matches the date/shift shown beneath it (no drift).
					const label = weekDaysLabels[(index + offset) % DAYS_PER_WEEK];
					return (
						<Box
							key={`weekday-${index}`}
							sx={{
								backgroundColor: SURFACE_TINT.LILAC_30,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: INK.SECONDARY,
								typography: "body2",
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
							}}
						>
							{label}
						</Box>
					);
				})}
			</Box>
			{/* Space kept while hidden, so the grid doesn't shift when a load starts or ends. */}
			<LinearProgress
				aria-label={t("loading.shifts")}
				data-testid="profile-weekly-schedule-loading-more"
				sx={{ visibility: isLoadingMoreWeeks ? "visible" : "hidden", mb: `${PADDING_PX}px` }}
			/>
			<VirtualizedList
				sx={{ width: "100%", height: typeof viewportHeight === "number" ? `${viewportHeight}px` : viewportHeight }}
				items={weeks}
				rowHeight={ROW_HEIGHT}
				renderItem={renderWeekRow}
				overscanCount={1}
				listRef={setList}
				onRowsRendered={handleRowsRendered}
			/>
		</Box>
	);
};
